import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import GlassCard from './GlassCard';

/**
 * THE label/value standard for detail pages.
 *
 * Every piece of read-only metadata in the app (matter details, task details,
 * contact details, project/playbook/data-source details, employee/team/
 * department details, …) renders through these three primitives so labels and
 * values have ONE size, ONE color, ONE rhythm - changed in one place.
 *
 *   <FieldGrid columns={3}>
 *     <FactTile icon={<PiTagLight />} label={t('Type')} value={matter.type} />
 *     <FactTile icon={<PiGlobeLight />} label={t('Jurisdiction')}>
 *       <span>…custom value…</span>
 *     </FactTile>
 *   </FieldGrid>
 *
 *   <GlassCard title={t('Process & Stage')}>
 *     <div className="space-y-4">
 *       <Field label={t('Status')}><Badge …/></Field>
 *       <Field label={t('Category')} value={matter.category} />
 *     </div>
 *   </GlassCard>
 *
 * Canonical recipe (do not re-implement inline):
 *   label: text-[11px] font-medium text-gray-400 dark:text-gray-500 (+ icon h-3.5 w-3.5)
 *   value: text-sm font-medium text-gray-700 dark:text-gray-200
 *   empty: text-sm text-gray-400 dark:text-gray-500 ("Not set")
 */

export type FieldTone = 'default' | 'success' | 'warning' | 'danger' | 'info';

const LABEL_TONE: Record<FieldTone, string> = {
  default: 'text-gray-400 dark:text-gray-500',
  success: 'text-success-400',
  warning: 'text-warning-400',
  danger: 'text-danger-400',
  info: 'text-info-400',
};

const VALUE_TONE: Record<FieldTone, string> = {
  default: 'text-gray-700 dark:text-gray-200',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  danger: 'text-danger-600 dark:text-danger-400',
  info: 'text-info-600 dark:text-info-300',
};

export interface FieldProps {
  /** Already-translated label text */
  label: string;
  /** Optional small icon rendered before the label */
  icon?: React.ReactNode;
  /** Plain-string value. Falsy → the standard muted "Not set". */
  value?: React.ReactNode;
  /** Custom value node (Badges, flags, avatars…). Takes precedence over `value`. */
  children?: React.ReactNode;
  /** Status coloring for label+value (due dates, alerts). Default 'default'. */
  tone?: FieldTone;
  /** Override the empty placeholder ("Not set"). */
  emptyText?: string;
  className?: string;
}

/** Label-over-value unit. Use inside GlassCard bodies and FactTiles. */
export const Field: React.FC<FieldProps> = ({
  label,
  icon,
  value,
  children,
  tone = 'default',
  emptyText,
  className,
}) => {
  const { t } = useTranslation();
  const content = children ?? value;
  return (
    <div className={className}>
      <div className={clsx('flex items-center gap-1.5', LABEL_TONE[tone])}>
        {icon && <span className="[&>svg]:h-3.5 [&>svg]:w-3.5 flex-shrink-0">{icon}</span>}
        <span className="text-[11px] font-medium">{label}</span>
      </div>
      <div className="mt-1.5">
        {content || content === 0 ? (
          typeof content === 'string' || typeof content === 'number' ? (
            <p className={clsx('text-sm font-medium', VALUE_TONE[tone])}>{content}</p>
          ) : (
            content
          )
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">{emptyText ?? t('Not set')}</p>
        )}
      </div>
    </div>
  );
};

export interface FactTileProps extends FieldProps {
  'data-tour-id'?: string;
}

/** A Field in its own small glass card - the Type/Jurisdiction/Language tiles. */
export const FactTile: React.FC<FactTileProps> = ({ className, ...field }) => {
  const { 'data-tour-id': tourId, ...fieldProps } = field as FactTileProps;
  return (
    <GlassCard padding="md" className={className} data-tour-id={tourId}>
      <Field {...fieldProps} />
    </GlassCard>
  );
};

export interface FieldGridProps {
  children: React.ReactNode;
  /** Columns at the widest step. Default 3. */
  columns?: 2 | 3 | 4;
  /**
   * What the grid measures itself against.
   *
   * `'viewport'` (default) collapses on the WINDOW's width - right for a grid
   * on an ordinary full-width page, wrong inside a detail surface, where a
   * 320px dock on a 1600px monitor still reports "sm and up" and keeps three
   * columns in a column that fits one.
   *
   * `'container'` collapses on the nearest `.details-canvas` / `.details-side`
   * instead (see index.css). Only meaningful with such an ancestor - without
   * one the container query never matches and the grid stays single-column —
   * so it is opt-in rather than the default.
   */
  measure?: 'viewport' | 'container';
  className?: string;
}

const GRID_COLS: Record<NonNullable<FieldGridProps['columns']>, string> = {
  2: 'grid grid-cols-1 sm:grid-cols-2 gap-3',
  3: 'grid grid-cols-1 sm:grid-cols-3 gap-3',
  4: 'grid grid-cols-2 sm:grid-cols-4 gap-3',
};

const CONTAINER_COLS: Record<NonNullable<FieldGridProps['columns']>, string> = {
  2: 'details-grid-2 gap-3',
  3: 'details-grid-3 gap-3',
  // 1 → 2 → 4 is exactly the KPI strip's progression.
  4: 'details-kpis gap-3',
};

/** The standard responsive grid for FactTiles / Fields. */
export const FieldGrid: React.FC<FieldGridProps> = ({
  children,
  columns = 3,
  measure = 'viewport',
  className,
}) => (
  <div
    className={clsx(
      measure === 'container' ? CONTAINER_COLS[columns] : GRID_COLS[columns],
      className
    )}
  >
    {children}
  </div>
);

export interface MetaTileProps {
  /** Leading icon (rendered at h-5 w-5, muted) */
  icon: React.ReactNode;
  /** Already-translated label text */
  label: string;
  /** Plain-string value. Falsy → the standard muted "Not set". */
  value?: React.ReactNode;
  /** Custom value node. Takes precedence over `value`. */
  children?: React.ReactNode;
  tone?: FieldTone;
  emptyText?: string;
  className?: string;
}

/** Horizontal icon-left glass tile - compact metadata strips (assignee, dates…). */
export const MetaTile: React.FC<MetaTileProps> = ({
  icon,
  label,
  value,
  children,
  tone = 'default',
  emptyText,
  className,
}) => {
  const { t } = useTranslation();
  const content = children ?? value;
  return (
    <div className={clsx('glass flex items-center gap-3 rounded-lg shadow-sm px-4 py-3', className)}>
      <span className={clsx('[&>svg]:h-5 [&>svg]:w-5 flex-shrink-0', LABEL_TONE[tone])}>{icon}</span>
      <div className="min-w-0">
        <p className={clsx('text-[11px] font-medium', LABEL_TONE[tone])}>{label}</p>
        {content || content === 0 ? (
          typeof content === 'string' || typeof content === 'number' ? (
            <p className={clsx('text-sm truncate', tone === 'default' ? 'text-gray-700 dark:text-gray-200' : VALUE_TONE[tone])}>{content}</p>
          ) : (
            content
          )
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">{emptyText ?? t('Not set')}</p>
        )}
      </div>
    </div>
  );
};

export interface KpiTileProps {
  /** The big number/value (top) */
  value: React.ReactNode;
  /** Already-translated label under the value */
  label: string;
  /**
   * Already-translated qualifier under the label ("3 admins", "of 25").
   *
   * A strip whose tiles disagree about having one is a strip whose baselines
   * disagree, so give every tile in a strip a hint or give none of them one.
   */
  hint?: string;
  /** Optional leading icon */
  icon?: React.ReactNode;
  tone?: FieldTone;
  className?: string;
}

/** Value-on-top KPI cell - use inside a KPI strip (grid + divide-x) or alone. */
export const KpiTile: React.FC<KpiTileProps> = ({
  value,
  label,
  hint,
  icon,
  tone = 'default',
  className,
}) => (
  <div className={clsx('p-4 flex items-center gap-3', className)}>
    {icon && <span className={clsx('[&>svg]:h-5 [&>svg]:w-5 flex-shrink-0', LABEL_TONE[tone])}>{icon}</span>}
    <div className="min-w-0 flex-1">
      <p className={clsx(
        'text-lg font-semibold truncate tabular-nums',
        tone === 'default' ? 'text-gray-900 dark:text-gray-100' : VALUE_TONE[tone]
      )}>
        {value}
      </p>
      {/* KPI labels carry a trailing qualifier ("Documents · 12 authorities")
          and are the first thing to overflow when the tile narrows. Clipped
          with the full text on hover, rather than wrapping and shoving the
          strip's rows out of alignment with each other. */}
      <p
        className="text-xs text-gray-500 dark:text-gray-400 truncate"
        title={label}
      >
        {label}
      </p>
      {hint && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate" title={hint}>
          {hint}
        </p>
      )}
    </div>
  </div>
);

export interface InfoRowProps {
  /** Already-translated label text (left, muted) */
  label: string;
  /** Plain-string value (right). Falsy → em dash. */
  value?: React.ReactNode;
  /** Custom value node. Takes precedence over `value`. */
  children?: React.ReactNode;
  className?: string;
}

/** Horizontal label ⟷ value row - audit metadata, quiet key/value lists. */
export const InfoRow: React.FC<InfoRowProps> = ({ label, value, children, className }) => {
  const content = children ?? value;
  return (
    <div className={clsx('flex items-center justify-between gap-3 text-sm', className)}>
      <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{label}</span>
      {content || content === 0 ? (
        typeof content === 'string' || typeof content === 'number' ? (
          <span className="text-gray-700 dark:text-gray-200 truncate">{content}</span>
        ) : (
          content
        )
      ) : (
        <span className="text-gray-400 dark:text-gray-500">—</span>
      )}
    </div>
  );
};

export interface WellProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
}

/** The standard sunken inner row INSIDE a glass card (readable over glass). */
export const Well: React.FC<WellProps> = ({ children, className, ...rest }) => (
  <div
    className={clsx('p-3 bg-surface-sunken/70 rounded-md border border-hairline-soft', className)}
    {...rest}
  >
    {children}
  </div>
);

export default Field;
